import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';

/**
 * Fetches / creates / deletes the current user's saved searches for a given
 * dashboard ("imports" / "exports" / "local"). Backed by the per-user
 * `user_saved_searches` Mongo collection via /api/user/saved-searches.
 *
 * The hook keeps the list in component state and exposes pure callbacks so
 * the consumer (e.g. the filter-bar popover) never has to think about
 * cache invalidation: every mutation refreshes the list optimistically.
 *
 * `filtersSelector` lets consumers control exactly what subset of state is
 * persisted (we don't want to save `selectedShipments` or pagination cursors).
 */
export const useSavedSearches = ({ api, dashboard = 'imports' }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!api) return;
    setLoading(true);
    try {
      const res = await api.get('/user/saved-searches', { params: { dashboard } });
      setItems(res.data?.items || []);
    } catch (e) {
      // Network failure is non-blocking — just leave the list empty.
      // Don't toast on initial load; the popover is opt-in.
      console.warn('[saved-searches] list failed:', e?.message);
    } finally {
      setLoading(false);
    }
  }, [api, dashboard]);

  useEffect(() => { refresh(); }, [refresh]);

  /** Save (or update) a named search. `filters` is the snapshot to persist. */
  const save = useCallback(async (name, filters) => {
    const cleanName = (name || '').trim();
    if (!cleanName) {
      toast.error('Give your search a name');
      return null;
    }
    try {
      const res = await api.post('/user/saved-searches', {
        name: cleanName,
        dashboard,
        filters,
      });
      await refresh();
      toast.success(`Saved "${cleanName}"`);
      return res.data;
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not save search');
      return null;
    }
  }, [api, dashboard, refresh]);

  /** Delete a saved search by id. */
  const remove = useCallback(async (id) => {
    if (!id) return false;
    try {
      await api.delete(`/user/saved-searches/${id}`);
      setItems((prev) => prev.filter((s) => s.id !== id));
      return true;
    } catch (e) {
      toast.error('Could not delete saved search');
      return false;
    }
  }, [api]);

  return { items, loading, save, remove, refresh };
};
